/**
 * Everyone round one phone.
 *
 * Excalibur is a hidden-hand game, so the handoff screen is not a nicety — it is
 * the only thing standing between the next player and four chips they are not
 * allowed to see. Nothing of the active player's view is mounted until the hold
 * completes, so there is no frame in which the previous player could glance down
 * and catch it.
 *
 * The real game plays its Draw and Play steps simultaneously. On one device they
 * become sequential, which is the standard adaptation and costs only the small
 * information edge of moving later.
 */

import { useCallback, useRef, useState } from 'react';
import {
  applyAction,
  newGame,
  viewFor,
  waitingOn,
  RuleError,
  type GameAction,
  type GameState,
  type SetupPlayer,
} from '@shared/engine';
import { Table } from '../game/Table';
import { HoldButton } from '../ui/HoldButton';
import { Screen, Toast } from '../ui/shell';

export function PassPlay({ names, onExit }: { names: string[]; onExit: () => void }) {
  const [, bump] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** Who is currently holding the phone. Null means "hand it over first". */
  const [holder, setHolder] = useState<string | null>(null);
  const stateRef = useRef<GameState | null>(null);

  if (!stateRef.current) stateRef.current = deal(names);
  const state = stateRef.current!;

  // Not memoised: the engine mutates `state` in place, so its object identity
  // never changes and any dependency array built from it would be a lie. It is a
  // filter over at most eight players — recomputing per render is free.
  const next = waitingOn(state)[0] ?? null;

  const act = useCallback((action: GameAction) => {
    const s = stateRef.current;
    if (!s || !holder) return;
    try {
      applyAction(s, holder, action);
    } catch (err) {
      setError(err instanceof RuleError ? err.message : 'That move did not take.');
      return;
    }
    // The phone goes back on the table the instant this player stops being the
    // one the game is waiting on.
    const stillMine = waitingOn(s)[0] === holder;
    if (!stillMine) setHolder(null);
    bump((n) => n + 1);
  }, [holder]);

  const again = useCallback(() => {
    stateRef.current = deal(names);
    setHolder(null);
    bump((n) => n + 1);
  }, [names]);

  if (state.phase === 'over') {
    // Nothing is secret any more, so the last screen needs no handoff. It is
    // rendered from the first seat's view purely because `Table` needs one; every
    // hand is public in `over`.
    return (
      <Table
        view={viewFor(state, state.players[0]!.id)}
        act={() => {}}
        onExit={onExit}
        endActions={
          <button type="button" className="btn" onClick={again}>
            Deal again
          </button>
        }
      />
    );
  }

  if (!holder) {
    const who = next ? state.players.find((p) => p.id === next) : null;
    if (!who) {
      return (
        <Screen eyebrow="Pass and play" title="Nobody can move">
          <p>The round has nothing left to ask for. This is a bug — please say so.</p>
        </Screen>
      );
    }
    return (
      <Screen
        eyebrow="Pass and play"
        title={`Pass to ${who.name}`}
        hot
        actions={
          <>
            <HoldButton label={`Hold to see your hand, ${who.name}`} onComplete={() => setHolder(who.id)} />
            <button type="button" className="btn ghost" onClick={onExit}>
              End the game
            </button>
          </>
        }
      >
        <p className="handoff-note">Everyone else, look away.</p>
        <p className="muted">
          {state.phase === 'draw'
            ? 'They are drawing back up to four chips.'
            : state.phase === 'play'
              ? 'They are choosing a chip to conceal.'
              : 'Their chip is resolving.'}
        </p>
      </Screen>
    );
  }

  return (
    <>
      <Table
        view={viewFor(state, holder)}
        act={act}
        onExit={onExit}
        banner={
          <div className="banner">
            <span>{state.players.find((p) => p.id === holder)?.name}&rsquo;s turn</span>
            <button type="button" className="linkish" onClick={() => setHolder(null)}>
              Put it down
            </button>
          </div>
        }
      />
      <Toast text={error} onDone={() => setError(null)} />
    </>
  );
}

function deal(names: string[]): GameState {
  const setup: SetupPlayer[] = names.map((n, i) => ({ id: `s${i}`, name: n, isBot: false }));
  const seed = crypto.getRandomValues(new Uint32Array(1))[0]!;
  return newGame(setup, seed);
}
